/**
 * The /auth routes (docs/AUTH.md "API"): email code start and verify, Google, logout. One interface, two
 * implementations: HTTP against the Bun API here, and the in-browser demo in src/mock/api.ts (createMockAuth).
 * Errors come back as ApiError with the contract's plain messages, so every screen shows the same words.
 */
import { ApiError, buildUrl, type ApiErrorBody } from "../api.ts";
import { attribution } from "./siteAnalytics.ts";

export interface SessionUser { id: string; email: string; name: string }
export interface SessionGrant { token: string; user: SessionUser }
/** `demoCode`: only the in-browser demo sends it, so the code screen can show it (no inbox in the demo). */
export interface EmailStartResult { ok: true; sentTo: string; demoCode?: string }

export interface SigninConfig { google: boolean }

export interface AuthClient {
  emailStart(email: string): Promise<EmailStartResult>;
  emailVerify(email: string, code: string): Promise<SessionGrant>;
  google(idToken: string): Promise<SessionGrant>;
  logout(token: string): Promise<void>;
  /** POST /auth/logout-all: ends every session of this person, this one too ("Sign out of all devices"). */
  logoutAll(token: string): Promise<{ revoked: number }>;
  /** GET /auth/config: which sign-in buttons work on this server. Google shows only when this says so. */
  signinConfig?(): Promise<SigninConfig>;
  /** GET /referrals/from/:code: the first name of who invited you ("Sam invited you" on sign-up). */
  referrerOf?(code: string): Promise<{ firstName: string }>;
  /** Demo only: whether a token is still a live session (the HTTP API answers 401 on the next call instead). */
  check?(token: string): boolean;
}

export const AUTH_MESSAGES = {
  wrong_code: "That code isn't right. Check the email and try again.",
  expired: "That code expired. Send a new one.",
  too_many_tries: "Too many tries. Send a new code.",
  signed_out: "Your sign-in expired. Please sign in again.",
  rate_limited: "Too many codes sent. Wait a while, then try again.",
  bad_email: "Type an email like you@example.com.",
  google_unavailable: "Google sign-in isn't turned on yet. Use \"Email me a code\".",
  offline: "We couldn't reach CleanCleaver. Check the internet and try again.",
} as const;

export const isEmail = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());

/** The plain words for any sign-in error: the contract's reason first, then the server's message. */
export function authMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const r = e.reason as keyof typeof AUTH_MESSAGES | undefined;
    if (r && r in AUTH_MESSAGES) return AUTH_MESSAGES[r];
    // A refused Google sign-in is a 401 too, but nobody was signed in: say why, never "your sign-in expired".
    if (e.reason === "google_refused") return e.message;
    if (e.status === 401) return AUTH_MESSAGES.signed_out;
    if (e.status === 429) return AUTH_MESSAGES.rate_limited;
    if (e.code === "google_unavailable" || e.status === 503) return AUTH_MESSAGES.google_unavailable;
    return e.message;
  }
  if (e instanceof TypeError) return AUTH_MESSAGES.offline; // fetch's network failure
  return e instanceof Error ? e.message : String(e);
}

export function createHttpAuth(baseUrl: string, doFetch: (url: string, init: RequestInit) => Promise<Response> = (u, i) => fetch(u, i)): AuthClient {
  async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json", "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await doFetch(buildUrl(baseUrl, path), { method: "POST", headers, body: JSON.stringify(body ?? {}) });
    if (!res.ok) {
      let code = "error", message = `Request failed (${res.status}).`, reason: string | undefined;
      try {
        const j = (await res.json()) as Partial<ApiErrorBody>;
        if (j.error) { code = j.error.code; message = j.error.message ?? message; reason = j.error.reason; }
      } catch { /* not JSON */ }
      throw new ApiError(res.status, code, message, reason);
    }
    try { return (await res.json()) as T; } catch { return {} as T; }
  }
  async function get<T>(path: string): Promise<T> {
    const res = await doFetch(buildUrl(baseUrl, path), { method: "GET", headers: { Accept: "application/json" } });
    if (!res.ok) throw new ApiError(res.status, "error", `Request failed (${res.status}).`);
    return (await res.json()) as T;
  }
  return {
    signinConfig: async () => ({ google: (await get<Partial<SigninConfig>>("/auth/config")).google === true }),
    referrerOf: (code) => get(`/referrals/from/${encodeURIComponent(code)}`),
    emailStart: (email) => post("/auth/email/start", { email: email.trim().toLowerCase() }),
    emailVerify: (email, code) => post("/auth/email/verify", { email: email.trim().toLowerCase(), code, ...attribution() }),
    google: (idToken) => post("/auth/google", { idToken, ...attribution() }),
    logout: async (token) => { await post("/auth/logout", {}, token); },
    logoutAll: async (token) => ({ revoked: (await post<{ revoked?: number }>("/auth/logout-all", {}, token)).revoked ?? 0 }),
  };
}
